import { Box, Loader, Text } from '@mantine/core';
import { FeatureCollection, MultiPolygon } from 'geojson';
import { MapMouseEvent } from 'mapbox-gl';
import { useEffect, useMemo, useState } from 'react';
import { Popup } from 'react-map-gl';

interface UseWmsLayerInfoOpts {
  imageryIndex: string;
  wmsSrc?: string;
}

const imageryIndexMaps = (index: string) => {
  switch (index) {
    case 'NDVI':
      return 'NDVI_DISPLAY_WFS';
    case 'LAI':
      return 'LAI2';
    default:
      return null;
  }
};

function toWebMercator(lon: number, lat: number): [number, number] {
  const x = (lon * 20037508.34) / 180;
  let y = Math.log(Math.tan(((90 + lat) * Math.PI) / 360)) / (Math.PI / 180);
  y = (y * 20037508.34) / 180;
  return [x, y];
}

export const useWmsLayerInfo = (opts: UseWmsLayerInfoOpts) => {
  const [markerPos, setMarkerPos] = useState<{
    lng: number;
    lat: number;
    data: string | null;
    isPending: boolean;
  } | null>(null);

  const handleWmsLayerClick = (e: MapMouseEvent) => {
    const id = imageryIndexMaps(opts.imageryIndex);
    const bbox = e.target.getBounds();
    const map = e.target;

    if (id && opts.wmsSrc && bbox) {
      setMarkerPos({
        lng: e.lngLat.lng,
        lat: e.lngLat.lat,
        data: null,
        isPending: true,
      });
      // Get the map container size
      const containerSize = map.getContainer().getBoundingClientRect();
      const width = Math.round(containerSize.width);
      const height = Math.round(containerSize.height);

      // Get the click point as pixel coordinates
      const point = map.project(e.lngLat);
      const x = Math.round(point.x);
      const y = Math.round(point.y);

      const wmsUrl = new URL(opts.wmsSrc);

      const sw = toWebMercator(bbox._sw.lng, bbox._sw.lat);
      const ne = toWebMercator(bbox._ne.lng, bbox._ne.lat);
      const epsg3857Bbox = `${sw[0]},${sw[1]},${ne[0]},${ne[1]}`;
      wmsUrl.searchParams.set('BBOX', epsg3857Bbox);
      wmsUrl.searchParams.delete('FORMAT');
      wmsUrl.searchParams.delete('Geometry');
      wmsUrl.searchParams.set('INFO_FORMAT', 'application/json');
      wmsUrl.searchParams.delete('LAYERS');
      wmsUrl.searchParams.set('QUERY_LAYERS', id);
      wmsUrl.searchParams.set('REQUEST', 'GetFeatureInfo');
      wmsUrl.searchParams.set('I', x.toString());
      wmsUrl.searchParams.set('J', y.toString());
      wmsUrl.searchParams.set('WIDTH', width.toString());
      wmsUrl.searchParams.set('HEIGHT', height.toString());

      // Fetch the WMS layer info
      fetch(wmsUrl.href)
        .then((response) => {
          if (!response.ok) {
            throw new Error('Network response was not ok');
          }
          return response.json();
        })
        .then((data: FeatureCollection<MultiPolygon>) => {
          setMarkerPos({
            lng: e.lngLat.lng,
            lat: e.lngLat.lat,
            data: data.features[0].properties?.out1,
            isPending: false,
          });
          // Handle the WMS layer info here
        })
        .catch((error) => {
          setMarkerPos(null);
        });
    }
  };

  const popup = useMemo(() => {
    return markerPos ? (
      <Popup
        anchor="bottom"
        key={markerPos.data}
        latitude={markerPos.lat}
        longitude={markerPos.lng}
        onClose={() => setMarkerPos(null)}
      >
        <Box mt="sm">
          {markerPos.isPending ? (
            <Loader size="sm" />
          ) : (
            <Text>
              {opts.imageryIndex} : {Number(markerPos.data).toFixed(2)}
            </Text>
          )}
        </Box>
      </Popup>
    ) : null;
  }, [markerPos, opts.imageryIndex]);

  useEffect(() => {
    if (opts.imageryIndex) {
      setMarkerPos(null);
    }
  }, [opts.imageryIndex]);

  return {
    handleWmsLayerClick,
    wmsInfoPopup: popup,
  };
};
